import Router from 'next/router';
import pkg from '../package.json';
import {
  CONSENT_MODES,
  CONSENT_STATUS,
  DEFAULT_OPTIONS,
  GUEST_REGISTERED_EVENT,
  IDENTIFY_WAIT_MS,
  LOGOUT_EVENT,
  PLUGIN_NAME
} from './constants';
import initPosthog, { applyConsent, clearStoredIdentity, registerSuperProperties } from './posthog';
import subscribeToConsent, { hasDidomiIds } from './consent';
import identifyGuest from './identify';
import { getSearchFromUrl, getSessionParams } from './session-params';

const getOptions = (options = {}) => ({
  ...DEFAULT_OPTIONS,
  ...Object.keys(options).reduce(
    (acc, key) =>
      options[key] === undefined || options[key] === '' ? acc : { ...acc, [key]: options[key] },
    {}
  )
});

export default async function load(app, pluginOptions = {}) {
  const options = getOptions(pluginOptions);

  if (typeof window !== 'undefined') {
    if (!options.apiKey) {
      app.log.warn(`${PLUGIN_NAME}: no PostHog apiKey, PostHog is not loaded`);
    } else {
      const {
        consent = {},
        captureOnReject,
        superProperties,
        sessionParams,
        sessionParamsPrefix
      } = options;
      if (consent.mode === CONSENT_MODES.DIDOMI && !hasDidomiIds(consent)) {
        app.log.warn(`${PLUGIN_NAME}: no Didomi purposes or vendors, PostHog captures nothing`);
      }

      // latest status from Didomi, applied when PostHog loads and after posthog.reset(), which
      // clears PostHog's consent
      let consentStatus = CONSENT_STATUS.PENDING;
      let posthogClient = null;
      // set once initialisation starts, so events while posthog-js is loading are not lost
      let posthogLoading = null;

      // url params registered for the session, kept to register them again
      let sessionProperties = {};
      const addSessionParams = search => {
        const properties = getSessionParams(search, sessionParams, sessionParamsPrefix);
        sessionProperties = { ...sessionProperties, ...properties };
        return properties;
      };
      // a logout before PostHog has loaded, reset in its loaded callback before its first pageview
      let resetOnLoad = false;
      const resetAfterLogout = posthog => {
        resetOnLoad = false;
        posthog.reset();
        // the visitor's choice is unchanged, so no second $opt_in event
        applyCurrentConsent(posthog, { captureOptInEvent: false });
      };

      // Consent first, then the properties: a rejection in cookieless mode (and posthog.reset())
      // clears PostHog's registered and session properties, and registering after the consent is
      // applied means they are never written under a stale consent
      const applyCurrentConsent = (posthog, { captureOptInEvent } = {}) => {
        applyConsent(posthog, consentStatus, {
          captureOnReject,
          captureOptInEvent,
          optInProperties: { ...superProperties, ...sessionProperties }
        });
        registerSuperProperties(posthog, superProperties);
        if (Object.keys(sessionProperties).length) {
          posthog.register_for_session(sessionProperties);
        }
      };
      // the landing url params are read before consent is applied, so they are on the $opt_in event
      // too, and registered with the other properties before the first pageview. A logout before
      // PostHog was initialised is applied first, so a stored identity is not used
      const onLoaded = posthog => {
        addSessionParams(window.location.search);
        if (resetOnLoad) {
          resetAfterLogout(posthog);
          return;
        }
        applyCurrentConsent(posthog);
      };

      // PostHog is only initialised once Didomi reports its status, so PostHog's own stored consent
      // from an earlier visit is never used: the current status is applied in posthog-js' loaded
      // callback, before its first pageview. If Didomi never loads, PostHog is never initialised
      subscribeToConsent(consent, status => {
        consentStatus = status;

        if (posthogClient) {
          applyCurrentConsent(posthogClient);
          return;
        }
        if (posthogLoading) return;

        posthogLoading = initPosthog(options, {
          onLoaded,
          // on every event, including those posthog-js sends inside a consent change
          getEventProperties: () => ({ ...superProperties, ...sessionProperties })
        })
          .then(posthog => {
            posthogClient = posthog;
            // in case the status changed while posthog-js was loading
            applyCurrentConsent(posthog);
            return posthog;
          })
          .catch(error => {
            app.log.error(`${PLUGIN_NAME}: PostHog failed to load`, error);
            return null;
          });
      });

      // The loaded client, waiting for posthog-js if it is loading. Null if it is not loading (Didomi
      // has not reported), failed, or takes longer than the wait
      const getPosthog = (wait = 0) => {
        if (posthogClient || !posthogLoading) return Promise.resolve(posthogClient);
        if (!wait) return posthogLoading;
        return Promise.race([
          posthogLoading,
          new Promise(resolve => {
            setTimeout(() => resolve(null), wait);
          })
        ]);
      };

      // url params on client side navigation, e.g. a link to ?offer=xmas26. beforeHistoryChange
      // runs before history.pushState, which is when PostHog captures the navigation's pageview, so
      // with a loaded client they are registered in time for it. They are kept straight away, so
      // before PostHog has loaded (or before Didomi has reported) they are registered once it has
      if (sessionParams && sessionParams.length) {
        Router.events.on('beforeHistoryChange', url => {
          const properties = addSessionParams(getSearchFromUrl(url));
          if (posthogClient && Object.keys(properties).length) {
            posthogClient.register_for_session(properties);
          }
        });
      }

      // awaited by the guest registration form before it logs in and redirects, so it waits for
      // posthog-js only if it is already loading, and not longer than IDENTIFY_WAIT_MS
      app.events.on(GUEST_REGISTERED_EVENT, async details => {
        const posthog = await getPosthog(IDENTIFY_WAIT_MS);
        if (!posthog) return;
        try {
          await identifyGuest(posthog, details);
        } catch (error) {
          app.log.error(`${PLUGIN_NAME}: PostHog identify failed`, error);
        }
      });

      // so the next person on a shared device is not linked to this one. reset() also clears
      // PostHog's consent status and registered properties, so both are applied again
      // Not awaited, so logout is not delayed. Before posthog-js has loaded (it is loading, or Didomi
      // has not reported) its stored identity is removed straight away, as the logout can be
      // followed by a page load, and the reset runs in its loaded callback before the first pageview
      app.events.on(LOGOUT_EVENT, () => {
        if (posthogClient) {
          resetAfterLogout(posthogClient);
          return;
        }
        resetOnLoad = true;
        clearStoredIdentity(options.persistenceName);
        if (posthogLoading) {
          // in case the loaded callback had already run
          posthogLoading.then(posthog => {
            if (posthog && resetOnLoad) resetAfterLogout(posthog);
          });
        }
      });
    }
  }

  return {
    name: PLUGIN_NAME,
    version: pkg.version
  };
}

load.pluginName = PLUGIN_NAME;
